import { Signal, inject } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { Store } from '@ngrx/store';

import { ToastService } from '@services/toast.service';
import { MassOperationType } from 'src/app/core/constants';
import {
  DataObject,
  ObjectDefinition,
  Workspace,
} from 'src/app/core/models/core.models';
import {
  massObjectOperationSuccess,
  moveObjectsSuccess,
} from 'src/app/core/actions/core.actions';
import { WorkspaceObjectsService } from 'src/app/core/services/workspace-objects.service';
import { augmentObjects } from 'src/app/shared/utils/object-utils';
import { CompanyApiService } from '../services/company-api.service';
import {
  UserApiService,
  UserObjectSearchCriteria,
} from '../services/user-api.service';
import { UtilsDataObjectApiService } from '../services/utils-data-object-api.service';
import { userWorkspacesQueriesFilter } from './workspace.queries';

/**
 * One object of the user's, by id. One entry per object, so any number of pickers
 * asking for overlapping id sets share rows.
 */
export const userObjectQueryKey = (objectId: string) =>
  ['user', 'object', objectId] as const;

/**
 * Batched, cache-first read of the user's objects by id: rows already cached are
 * served without a request, the **misses go out as one** POST /user/user_objects,
 * the result comes back in the *requested* order (the backend does not preserve
 * it), `forceRefresh` skips the cache read entirely, and a failed fetch degrades to
 * whatever was cached rather than erroring - the callers are pickers and form
 * displays, which are better off incomplete than broken.
 *
 * Imperative rather than a hook: every consumer fetches on an event (a picker
 * closes, a form loads) and two of them are inside services.
 */
export function injectFetchUserObjectsByIds() {
  const queryClient = inject(QueryClient);
  const userApi = inject(UserApiService);

  return async (
    objectIds: string[],
    forceRefresh = false,
  ): Promise<DataObject[]> => {
    const ids = objectIds ?? [];
    if (ids.length === 0) {
      return [];
    }

    const resolved = new Map<string, DataObject>();

    if (!forceRefresh) {
      for (const id of ids) {
        const cached = queryClient.getQueryData<DataObject>(
          userObjectQueryKey(id),
        );
        if (cached) {
          resolved.set(id, cached);
        }
      }
    }

    const missing = ids.filter((id) => !resolved.has(id));

    if (missing.length > 0) {
      try {
        const fetched = await firstValueFrom(
          userApi.getUserObjectsByIds(missing),
        );

        // Cached rows carry icon + imageUrl like the other object lists: the
        // picker displays bind fa-icon to `object.icon` and throw on undefined.
        for (const object of augmentObjects(fetched ?? [])) {
          queryClient.setQueryData(userObjectQueryKey(object.id), object);
          resolved.set(object.id, object);
        }
      } catch (error) {
        console.error('Error fetching user objects:', error);
      }
    }

    // * requested order; ids the server did not return are dropped, and
    // * deliberately not cached, so the next call retries them
    return ids
      .map((id) => resolved.get(id))
      .filter((object): object is DataObject => !!object);
  };
}

/** Mirrors POST /user/user_object_list. */
export const userObjectSearchQueryKey = (criteria: UserObjectSearchCriteria) =>
  ['user', 'object_search', criteria] as const;

/**
 * One search request, plus per-id cache warming: every row the search returns is a
 * row `injectFetchUserObjectsByIds` can then serve without a request. Lean rows
 * (`criteria.lean`) are partial objects and never warm that cache.
 */
const searchObjects = (
  userApi: UserApiService,
  queryClient: QueryClient,
  criteria: UserObjectSearchCriteria,
): Promise<DataObject[]> =>
  firstValueFrom(userApi.searchUserObjects(criteria)).then((objects) => {
    if (!criteria.lean) {
      for (const object of augmentObjects(objects ?? [])) {
        queryClient.setQueryData(userObjectQueryKey(object.id), object);
      }
    }

    return objects ?? [];
  });

/**
 * An object search, for the two consumers that **render** it reactively: quick
 * search's "all matches" list and the post-related dialog's feed dropdown.
 *
 * `augmentObjects` (icon + image) is applied here but **not** in the imperative tier
 * below, whose callers read raw rows and augment themselves where they need to.
 *
 * `staleTime` is the default 0: the criteria carry the search term, so a re-typed
 * term paints its previous rows immediately and refetches, instead of flashing an
 * empty list.
 */
export function injectUserObjectSearchQuery(
  criteria: Signal<UserObjectSearchCriteria | null>,
) {
  const userApi = inject(UserApiService);
  const queryClient = inject(QueryClient);

  return injectQuery(() => {
    const current = criteria();

    return {
      queryKey: current
        ? userObjectSearchQueryKey(current)
        : (['user', 'object_search', null] as const),
      queryFn: () =>
        searchObjects(userApi, queryClient, current!).then(
          (objects) => augmentObjects(objects) as DataObject[],
        ),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * The same search as a one-shot request, for the callers that search per keystroke:
 * the object-picker's list (one instance per tab), two type-aheads and the widget
 * sidebar's feed fallback.
 *
 * **Deliberately not cached** - a picker with a dozen workspace tabs would otherwise
 * leave a dozen list entries behind. `UserObjectsService.search$` adds the
 * degrade-to-`[]`.
 */
export function injectSearchUserObjects() {
  const userApi = inject(UserApiService);
  const queryClient = inject(QueryClient);

  return (criteria: UserObjectSearchCriteria) =>
    searchObjects(userApi, queryClient, criteria);
}

// The four operations whose response rows leave the list the user is looking at.
const MASS_OPERATIONS_REMOVING_ROWS = [
  'unarchive',
  'undelete',
  'delete',
  'archive',
];

/**
 * Archive / unarchive / delete / undelete objects.
 *
 * Imperative rather than a hook because one of its callers is a root service, which
 * api-layer.md bars from holding hooks; keeping it one function keeps the toasts,
 * the cache patches and the broadcast in one place. The removing operations take
 * the affected rows out of the cached workspace and workspace-object lists;
 * `massObjectOperationSuccess` survives for `selected.reducer`'s selection reset.
 */
export function injectMassObjectOperation() {
  const companyApi = inject(CompanyApiService);
  const queryClient = inject(QueryClient);
  const workspaceObjects = inject(WorkspaceObjectsService);
  const store = inject(Store);
  const toast = inject(ToastService);

  return (operation: MassOperationType, objects: ObjectDefinition[]) =>
    firstValueFrom(companyApi.massObjectOperation(operation, objects)).then(
      (response) => {
        if (MASS_OPERATIONS_REMOVING_ROWS.includes(response.operation)) {
          const removedIds = (response.data.workspaces ?? []).map((w) => w.id);
          if (removedIds.length) {
            queryClient.setQueriesData<Workspace[]>(
              userWorkspacesQueriesFilter(),
              (workspaces) =>
                workspaces?.filter((w) => !removedIds.includes(w.id)),
            );
          }

          workspaceObjects.removeObjects(response);
        }

        store.dispatch(massObjectOperationSuccess({ response }));

        // * hand-built English - there is no translation key for this yet
        toast.showSuccess(`Operation ${response.operation} was successful`);
        return response;
      },
      (error: Error) => {
        toast.showError(error.message);
        throw error;
      },
    );
}

/**
 * Move objects to another workspace. Component-only caller (the move dialog), so an
 * ordinary mutation hook. A move is silent in both directions by design. The hook
 * owns the cache merge (the response carries the new `workspace_id`) and still
 * broadcasts `moveObjectsSuccess` for `selected.reducer`'s selection reset.
 */
export function injectMoveObjectsMutation() {
  const utilsDataObjectApi = inject(UtilsDataObjectApiService);
  const workspaceObjects = inject(WorkspaceObjectsService);
  const store = inject(Store);

  return injectMutation(() => ({
    mutationFn: ({
      objects,
      workspaceId,
    }: {
      objects: DataObject[];
      workspaceId: string;
    }) =>
      firstValueFrom(
        utilsDataObjectApi.moveObjects(
          objects.map((object) => ({
            object_id: object.id,
            object_class_id: object.object_class_id,
            object_type_id: object.object_type_id,
          })),
          workspaceId,
        ),
      ),
    onSuccess: (objects) => {
      workspaceObjects.mergeMovedObjects(objects);
      store.dispatch(moveObjectsSuccess({ objects }));
    },
  }));
}
